// MandatorySurveyGate — responder la encuesta es un paso obligatorio.
// v6.11.329: reemplaza a RegularSurveyBlocker y PlayoffsSurveyBlocker, que sólo
// tapaban Inicio y la pestaña Playoffs de Partidos (se esquivaban yendo a Tabla,
// Equipo o Fase Regular). Ahora envuelve TODAS las secciones del jugador menos
// Perfil (para poder cerrar sesión o cambiar de edición).
//
// Qué encuesta obliga lo decide el servidor (`must_answer`, v6.11.329):
//   · REGULAR: a todos, cuando terminó la fase regular (no mientras se juega).
//   · PLAYOFFS: a la pareja que terminó sus playoffs (perdió o salió campeona),
//     nunca antes: el día de finales nadie queda trabado para ver su partido.
// Cada integrante responde por separado.
//
// El contenido va SIEMPRE en el mismo lugar del árbol (oculto mientras falta la
// encuesta): al responderla no se vuelve a montar y conserva su estado (p. ej.
// «compartir después de la encuesta» del festejo de playoffs).
// La historia de por qué bloquear: en la 4ta, la encuesta de playoffs sin
// bloqueo registró CERO respuestas; la regular, con bloqueo, llegó al 82,8%.

import React from 'react';
import { ClipboardList, Trophy } from 'lucide-react';
import { SurveyView } from '../SurveyView';
import { usePendingSurveys } from '../../hooks/usePendingSurveys';
import { RegularSurveyGateContext } from './regularSurveyGate';
import type { SurveyPhase } from '../../services/api/surveys';

interface Props {
  /** false = no bloquea (p. ej. en Perfil). */
  active: boolean;
  /**
   * Bracket publicado. Sólo para un servidor sin v6.11.329 (no manda
   * `must_answer`): ahí se mantiene el bloqueo de antes, la regular en playoffs.
   */
  bracketPublished: boolean;
  children: React.ReactNode;
}

// v6.11.330 — El tono que pidió el organizador: la Liga la hacemos entre todos.
const COPY: Record<SurveyPhase, { title: string; body: string }> = {
  REGULAR: {
    title: 'La Liga la hacemos entre todos 🙌',
    body: 'Contanos cómo viviste la fase regular. Sin tus sugerencias y tu opinión no podríamos mejorar edición tras edición. Son pocas preguntas y podés responder de forma anónima.',
  },
  PLAYOFFS: {
    title: '¿Cómo viviste los playoffs? 🏆',
    body: 'La Liga la hacemos entre todos: con tus sugerencias y tu opinión mejoramos edición tras edición. Son pocas preguntas y podés responder de forma anónima.',
  },
};

export const MandatorySurveyGate: React.FC<Props> = ({ active, bracketPublished, children }) => {
  const { pendingPhases, mustAnswer, refresh } = usePendingSurveys();

  const serverDecides = pendingPhases.some((p) => p.must_answer !== undefined);
  const legacy = !serverDecides && bracketPublished
    ? pendingPhases.find((p) => p.phase === 'REGULAR') ?? null
    : null;
  const phase = (mustAnswer ?? legacy)?.phase ?? null;
  const blocked = active && phase !== null;

  const content = (
    <RegularSurveyGateContext.Provider value={{ pending: blocked }}>
      <div
        className={blocked ? 'invisible pointer-events-none select-none' : undefined}
        aria-hidden={blocked ? true : undefined}
      >
        {children}
      </div>
    </RegularSurveyGateContext.Provider>
  );

  if (!blocked || !phase) return content;

  const Icon = phase === 'PLAYOFFS' ? Trophy : ClipboardList;
  return (
    <>
      {content}

      {/* Explicación detrás del modal de la encuesta */}
      <div className="fixed inset-0 z-[9990] bg-gradient-to-br from-primary/20 to-primary-dark/30 flex flex-col items-center justify-center p-6 text-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
          <Icon size={32} className="text-primary" />
        </div>
        <div className="max-w-xs mx-auto space-y-2">
          <h2 className="text-xl font-bold text-gray-900 font-display">{COPY[phase].title}</h2>
          <p className="text-sm text-gray-700 leading-snug">{COPY[phase].body}</p>
          <p className="text-xs text-gray-500">
            Cada integrante de la pareja la responde por separado. Apenas termines, seguís usando la app.
          </p>
        </div>
      </div>

      {/* Sin cierre posible: se sale respondiendo. `key` → si después de la
          regular queda la de playoffs, arranca de cero. */}
      <SurveyView
        key={phase}
        phase={phase}
        blocking={true}
        onClose={() => {}}
        onSubmitted={() => { refresh(); }}
      />
    </>
  );
};
